import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, useAction } from '../../../api/hooks';
import type { DispatchOutcome, ReleaseDetail } from '../types';

const dispatchMessage = (attempt: string, d: DispatchOutcome) =>
  d.state === 'dispatched'
    ? `${attempt} started — Jira ran the deploy workflow`
    : d.state === 'refused'
      ? `${attempt} failed: GitHub refused to run the workflow`
      : d.state === 'not_dispatched'
        ? `${attempt}: ${d.message ?? 'No workflow dispatched'}`
        : `${attempt} registered, but GitHub did not confirm the run`;

/** Keep action state mounted with the page, including shared rationale/reason text. */
export function useReleaseActions(
  key: string | undefined,
  releaseId: string | undefined,
  release: ReleaseDetail | undefined,
) {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<'edit' | 'request' | 'proceed' | 'cancel' | null>(null);
  const [text, setText] = useState('');
  const base = `/projects/${key}/releases/${releaseId}`;
  const inv = [['release', key, releaseId], ['releases', key], ['releasable', key], ['approvals']];
  const request = useAction(
    () => api.post(`${base}/request-approval`, { expectedVersion: release!.version, rationale: text }),
    { success: 'Approval requested', invalidate: inv, onSuccess: () => setDialog(null) },
  );
  const proceed = useAction(
    () =>
      api.post<{ attempt: string; dispatch: DispatchOutcome }>(`${base}/proceed`, {
        expectedVersion: release!.version,
      }),
    {
      success: (r) => dispatchMessage(r.attempt, r.dispatch),
      invalidate: inv,
      onSuccess: (r) => navigate(`${base}/attempts/${r.attempt}`),
    },
  );
  const refresh = useAction(() => api.post(`${base}/refresh`, {}), {
    success: 'Pull requests re-read from GitHub',
    invalidate: inv,
  });
  const cancel = useAction(
    () => api.post(`${base}/cancel`, { expectedVersion: release!.version, reason: text }),
    { success: 'Release cancelled', invalidate: inv, onSuccess: () => setDialog(null) },
  );

  return { base, inv, dialog, setDialog, text, setText, request, proceed, refresh, cancel };
}
export type ReleaseActions = ReturnType<typeof useReleaseActions>;
